// src/pages/VersionInfo.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';

export default function VersionInfo() {
  const [cacheInfo, setCacheInfo] = useState({
    name: 'در حال بارگذاری...',
    time: '',
    readable: '',
  });
  const [isChecking, setIsChecking] = useState(false);

  // خواندن اطلاعات نسخه
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await fetch('/service-worker.js?v=' + Date.now());
        const text = await res.text();

        const nameMatch = text.match(/const CACHE_NAME = "(pwa-eg-cache-v[^"]+)"/);
        const timeMatch = text.match(/const BUILD_TIME = "([^"]+)"/);

        if (nameMatch && timeMatch) {
          const buildTime = new Date(timeMatch[1]);
          const formatted = buildTime.toLocaleString('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });

          setCacheInfo({
            name: nameMatch[1],
            time: timeMatch[1],
            readable: formatted,
          });
        } else {
          setCacheInfo({ name: 'نسخه نامشخص', time: '', readable: '' });
        }
      } catch (err) {
        setCacheInfo({ name: 'خطا در اتصال', time: '', readable: '' });
      }
    };

    fetchVersion();
  }, []);

  // تابع بروزرسانی
  const handleUpdate = () => {
    setIsChecking(true);
    toast.info('در حال بررسی بروزرسانی...', { autoClose: 2000 });

    serviceWorkerRegistration.forceUpdate();

    setTimeout(() => {
      setIsChecking(false);
      toast.success('اگر نسخه جدیدی باشد، صفحه رفرش می‌شود...', { autoClose: 3000 });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-md mx-auto">

        {/* عنوان */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-8"
        >
          اطلاعات نسخه
        </motion.h1>

        {/* کارت اصلی */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          {/* هدر با گرادیانت */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
            <div className="flex items-center justify-center mb-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center"
              >
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m-8 6v4" />
                </svg>
              </motion.div>
            </div>
            <h2 className="text-xl font-bold text-center">اپلیکیشن Esf.gold</h2>
          </div>

          {/* اطلاعات نسخه */}
          <div className="p-6 space-y-5">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.07l.146.008c.306.037.586.15.806.367.206.201.368.457.492.73.105.23.196.475.258.73.065.26.11.526.126.79.01.158.01.317.01.476a1 1 0 102 0c0-.159 0-.318-.01-.476-.016-.264-.06-.53-.126-.79a6.07 6.07 0 00-.258-.73 2.07 2.07 0 00-.492-.73 2.07 2.07 0 00-.806-.367L8.93 15H11a2 2 0 002-2V5a1 1 0 10-2 0v8H5V5a1 1 0 10-2 0v8a1 1 0 001 1h1.07l.146.008c.306.037.586.15.806.367.206.201.368.457.492.73.105.23.196.475.258.73.065.26.11.526.126.79.01.158.01.317.01.476a1 1 0 102 0c0-.159 0-.318-.01-.476-.016-.264-.06-.53-.126-.79a6.07 6.07 0 00-.258-.73 2.07 2.07 0 00-.492-.73 2.07 2.07 0 00-.806-.367L5.93 15H5a1 1 0 01-1-1V5a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                <span className="font-bold text-indigo-800">نسخه فعلی</span>
              </div>
              <p className="font-mono text-indigo-700 text-sm break-all">{cacheInfo.name}</p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span className="font-bold text-purple-800">تاریخ ساخت</span>
              </div>
              <p className="text-purple-700 text-sm">{cacheInfo.readable || 'نامشخص'}</p>
            </div>

            {/* دکمه بروزرسانی */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleUpdate}
              disabled={isChecking}
              className={`w-full py-4 rounded-2xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-3 ${
                isChecking
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-xl'
              }`}
            >
              {isChecking ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                  در حال بررسی...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  بررسی بروزرسانی
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* فوتر */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-gray-500 text-xs mt-8"
        >
          © ۱۴۰۴ اپلیکیشن Esf.gold - نسخه {cacheInfo.name}
        </motion.p>
      </div>
    </div>
  );
}